import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ApiClientError } from "@/lib/api-client";
import { formatApiError } from "@/lib/format-api-error";
import { uploadFileMultipart } from "@/lib/upload";

// POST /api/portal/listing/media (multipart {file, kind}) → { url }.
//
// Third consumer of `uploadFileMultipart` (after admin tier badges): listing
// media is small (≤2 MB images) and the worker validates MIME/size + writes
// to the public listing-media bucket in one handler, so the atomic
// single-POST shape fits — the 3-step signed-URL flow would add an
// orphan-object failure mode for no benefit.
//
// No invalidation and no optimistic update: the returned URL lives in the
// form's local state until the partner submits the whole change request.
// Error toast lives here (upload is a self-contained side action, unlike the
// page-level submit); `uploadFileMultipart` throws ApiClientError, so
// formatApiError can surface the worker's message verbatim.
export type UploadListingMediaInput = {
  kind: "logo" | "hero";
  file: File;
};

export function useUploadListingMedia() {
  return useMutation<{ url: string }, ApiClientError, UploadListingMediaInput>({
    mutationFn: ({ kind, file }) =>
      uploadFileMultipart<{ url: string }>(file, {
        endpoint: "/portal/listing/media",
        extraFields: { kind },
      }),
    onError: (err) => {
      toast.error(formatApiError(err, "Failed to upload image"));
    },
  });
}
